import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { TableData } from '../types/table'

interface SpreadsheetTableProps {
  data: TableData
  onChange?: (data: TableData) => void
  onColumnSelect?: (columnIndex: number) => void
  selectedColumnIndex?: number | null
  onRowSelect?: (rowIndex: number) => void
  selectedRowIndex?: number | null
  isAIActivated?: boolean
}

export interface SpreadsheetTableRef {
  insertColumnLeft: (columnIndex: number) => void
  insertColumnRight: (columnIndex: number) => void
  insertRowAbove: (rowIndex: number) => void
  insertRowBelow: (rowIndex: number) => void
  removeRow: (rowIndex: number) => void
  getSelectedElementPosition: () => { top: number; left: number } | null
}

const SpreadsheetTable = forwardRef<SpreadsheetTableRef, SpreadsheetTableProps>(
  ({ data, onChange, onColumnSelect, selectedColumnIndex, onRowSelect, selectedRowIndex, isAIActivated }, ref) => {
  const [tableData, setTableData] = useState<TableData>(data)
  const [visibleRows, setVisibleRows] = useState<number>(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastAnimatedDataHashRef = useRef<string>('')
  const initialDataRef = useRef<TableData | null>(null)
  // Track which cells are currently being edited to prevent re-rendering
  const editingCellsRef = useRef<Set<string>>(new Set())
  const editingColumnsRef = useRef<Set<number>>(new Set())
  // Refs for selected elements to calculate position
  const selectedColumnHeaderRef = useRef<HTMLTableCellElement | null>(null)
  const selectedRowIndexRef = useRef<HTMLTableCellElement | null>(null)

  // Create a stable hash of the data structure to detect truly new AI data
  // Only tracks structural changes (column count, row count), not content changes
  const getDataHash = (data: TableData): string => {
    // Use column count and row count as the hash - this detects structural changes
    // but ignores content edits (cell values, column names)
    return `${data.columns.length}-${data.rows.length}`
  }

  // Sync with props when data changes, but only if cells are not being edited
  useEffect(() => {
    // Only update if no cells are currently being edited
    if (editingCellsRef.current.size === 0 && editingColumnsRef.current.size === 0) {
      // Preserve pinnedColumns and hiddenColumns if they exist
      setTableData(prev => ({
        ...data,
        pinnedColumns: prev?.pinnedColumns || data.pinnedColumns || [],
        hiddenColumns: prev?.hiddenColumns || data.hiddenColumns || []
      }))
    }
  }, [data])

  // Initialize contentEditable cells with span structure on mount
  useEffect(() => {
    const initializeCells = () => {
      const editableDivs = document.querySelectorAll('[contenteditable="true"]')
      editableDivs.forEach((div) => {
        if (div instanceof HTMLElement) {
          const table = div.closest('table')
          if (table && table.classList.contains('w-full')) {
            // Only initialize if span doesn't exist
            if (!div.querySelector('span.spreadsheet-table-cell-content')) {
              const textContent = div.innerText || ''
              const span = document.createElement('span')
              span.className = 'spreadsheet-table-cell-content'
              span.style.cssText = `
                direction: ltr !important;
                unicode-bidi: embed !important;
                text-align: left !important;
                white-space: normal !important;
                outline: none !important;
                display: inline-block !important;
              `
              span.textContent = textContent
              div.innerHTML = ''
              div.appendChild(span)
            }
          }
        }
      })
    }

    // Initialize after a short delay to ensure DOM is ready
    const timeout = setTimeout(initializeCells, 0)
    return () => clearTimeout(timeout)
  }, [tableData])

  // Row reveal animation effect - only runs for new AI data
  useEffect(() => {
    const currentDataHash = getDataHash(data)
    const isStructuralChange = currentDataHash !== lastAnimatedDataHashRef.current
    const isInitialData = initialDataRef.current === null

    // Initialize reference data on first render
    if (isInitialData) {
      initialDataRef.current = data
    }

    // If this is the same data structure (user edits), preserve current visible rows and skip animation
    if (!isStructuralChange && !isInitialData) {
      // User is editing - don't re-animate, just ensure all rows are visible
      const totalRows = data.rows.length
      if (visibleRows < totalRows) {
        setVisibleRows(totalRows)
      }
      return
    }

    // Clean up any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Mark this data structure as animated
    lastAnimatedDataHashRef.current = currentDataHash
    if (isInitialData) {
      initialDataRef.current = data
    }

    const totalRows = data.rows.length

    // If rows > 50, skip animation and render instantly
    if (totalRows > 50) {
      setVisibleRows(totalRows)
      return
    }

    // Reset visible rows for new data
    setVisibleRows(0)

    // If no rows, nothing to animate
    if (totalRows === 0) {
      return
    }

    // Reveal rows one by one
    let currentIndex = 0
    intervalRef.current = setInterval(() => {
      currentIndex++
      setVisibleRows(currentIndex)

      // Stop when all rows are visible
      if (currentIndex >= totalRows) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }, 120)

    // Cleanup on unmount or when data changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Ensure span structure is maintained in contentEditable cells
  useEffect(() => {
    const maintainSpanStructure = () => {
      const editableDivs = document.querySelectorAll('[contenteditable="true"]')
      editableDivs.forEach((div) => {
        if (div instanceof HTMLElement) {
          // Check if div is inside a spreadsheet table
          const table = div.closest('table')
          if (table && table.classList.contains('w-full')) {
            let span = div.querySelector('span.spreadsheet-table-cell-content')
            
            // If span doesn't exist, create it
            if (!span) {
              span = document.createElement('span')
              span.className = 'spreadsheet-table-cell-content'
              span.style.cssText = `
                direction: ltr !important;
                unicode-bidi: embed !important;
                text-align: left !important;
                white-space: normal !important;
                outline: none !important;
                display: inline-block !important;
              `
              // Move all child nodes into the span
              while (div.firstChild) {
                span.appendChild(div.firstChild)
              }
              div.appendChild(span)
            } else {
              // Ensure span has correct styles
              span.style.cssText = `
                direction: ltr !important;
                unicode-bidi: embed !important;
                text-align: left !important;
                white-space: normal !important;
                outline: none !important;
                display: inline-block !important;
              `
            }
          }
        }
      })
    }

    // Run immediately and set up observer
    maintainSpanStructure()
    
    // Use MutationObserver to maintain structure when DOM changes
    const observer = new MutationObserver(maintainSpanStructure)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    })

    return () => observer.disconnect()
  }, [data])

  // Handle cell value change
  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const newRows = [...tableData.rows]
    if (!newRows[rowIndex]) {
      newRows[rowIndex] = []
    }
    newRows[rowIndex] = [...newRows[rowIndex]]
    newRows[rowIndex][colIndex] = value

    const newData: TableData = {
      columns: [...tableData.columns],
      rows: newRows,
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Handle contentEditable cell blur (only update on blur, not on input)
  const handleCellBlur = (rowIndex: number, colIndex: number, event: React.FocusEvent<HTMLDivElement>) => {
    const cellKey = `${rowIndex}-${colIndex}`
    editingCellsRef.current.delete(cellKey)
    
    // Get text content from the div
    const value = event.currentTarget.innerText || ''
    handleCellChange(rowIndex, colIndex, value)
  }

  // Handle cell focus - mark as editing to prevent re-rendering
  const handleCellFocus = (rowIndex: number, colIndex: number) => {
    const cellKey = `${rowIndex}-${colIndex}`
    editingCellsRef.current.add(cellKey)
  }

  // Handle contentEditable column header blur (only update on blur, not on input)
  const handleColumnHeaderBlur = (colIndex: number, event: React.FocusEvent<HTMLDivElement>) => {
    editingColumnsRef.current.delete(colIndex)
    
    // Get text content from the div
    const value = event.currentTarget.innerText || ''
    handleColumnHeaderChange(colIndex, value)
  }

  // Handle column header focus - mark as editing to prevent re-rendering
  const handleColumnHeaderFocus = (colIndex: number) => {
    editingColumnsRef.current.add(colIndex)
  }

  // Handle column header change
  const handleColumnHeaderChange = (colIndex: number, value: string) => {
    const newColumns = [...tableData.columns]
    newColumns[colIndex] = value

    const newData: TableData = {
      columns: newColumns,
      rows: [...tableData.rows],
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Add a new row
  const handleAddRow = () => {
    const newRow = new Array(tableData.columns.length).fill('')
    const newRowNames = tableData.rowNames ? [...tableData.rowNames, ''] : undefined
    const newData: TableData = {
      columns: [...tableData.columns],
      rows: [...tableData.rows, newRow],
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns,
      rowNames: newRowNames
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Insert a row at a specific index
  const insertRowAt = (rowIndex: number) => {
    // Check if maximum rows limit reached
    if (tableData.rows.length >= 10) {
      return
    }
    
    const newRow = new Array(tableData.columns.length).fill('')
    const newRows = [...tableData.rows]
    newRows.splice(rowIndex, 0, newRow)
    
    const newRowNames = tableData.rowNames ? [...tableData.rowNames] : undefined
    if (newRowNames) {
      newRowNames.splice(rowIndex, 0, '')
    }
    
    const newData: TableData = {
      columns: [...tableData.columns],
      rows: newRows,
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns,
      rowNames: newRowNames
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Insert a row above the specified index
  const insertRowAbove = (rowIndex: number) => {
    insertRowAt(rowIndex)
  }

  // Insert a row below the specified index
  const insertRowBelow = (rowIndex: number) => {
    insertRowAt(rowIndex + 1)
  }

  // Remove a row
  const handleRemoveRow = (rowIndex: number) => {
    if (tableData.rows.length <= 1) return // Keep at least one row
    
    const newRows = tableData.rows.filter((_, index) => index !== rowIndex)
    const newRowNames = tableData.rowNames ? tableData.rowNames.filter((_, index) => index !== rowIndex) : undefined
    
    const newData: TableData = {
      columns: [...tableData.columns],
      rows: newRows,
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns,
      rowNames: newRowNames
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Add a new column
  const handleAddColumn = () => {
    const newColumnName = `Colonne ${tableData.columns.length + 1}`
    const newColumns = [...tableData.columns, newColumnName]
    const newRows = tableData.rows.map(row => [...row, ''])
    
    const newData: TableData = {
      columns: newColumns,
      rows: newRows,
      pinnedColumns: tableData.pinnedColumns,
      hiddenColumns: tableData.hiddenColumns
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Remove a column
  const handleRemoveColumn = (colIndex: number) => {
    if (tableData.columns.length <= 1) return // Keep at least one column
    
    const newColumns = tableData.columns.filter((_, index) => index !== colIndex)
    const newRows = tableData.rows.map(row => row.filter((_, index) => index !== colIndex))
    
    // Update pinned and hidden column indices
    const newPinnedColumns = (tableData.pinnedColumns || [])
      .filter(idx => idx !== colIndex)
      .map(idx => idx > colIndex ? idx - 1 : idx)
    const newHiddenColumns = (tableData.hiddenColumns || [])
      .filter(idx => idx !== colIndex)
      .map(idx => idx > colIndex ? idx - 1 : idx)
    
    const newData: TableData = {
      columns: newColumns,
      rows: newRows,
      pinnedColumns: newPinnedColumns,
      hiddenColumns: newHiddenColumns
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Insert column at specific position
  const insertColumnAt = (colIndex: number) => {
    // Check if maximum columns limit reached
    if (tableData.columns.length >= 10) {
      return
    }
    
    const newColumnName = `Colonne ${tableData.columns.length + 1}`
    const newColumns = [...tableData.columns]
    newColumns.splice(colIndex, 0, newColumnName)
    
    const newRows = tableData.rows.map(row => {
      const newRow = [...row]
      newRow.splice(colIndex, 0, '')
      return newRow
    })
    
    // Update pinned and hidden column indices
    const newPinnedColumns = (tableData.pinnedColumns || []).map(idx => idx >= colIndex ? idx + 1 : idx)
    const newHiddenColumns = (tableData.hiddenColumns || []).map(idx => idx >= colIndex ? idx + 1 : idx)
    
    const newData: TableData = {
      columns: newColumns,
      rows: newRows,
      pinnedColumns: newPinnedColumns,
      hiddenColumns: newHiddenColumns
    }
    setTableData(newData)
    onChange?.(newData)
  }

  // Insert column to the left of specified column
  const insertColumnLeft = (columnIndex: number) => {
    insertColumnAt(columnIndex)
  }

  // Insert column to the right of specified column
  const insertColumnRight = (columnIndex: number) => {
    insertColumnAt(columnIndex + 1)
  }
  
  // Get position of selected element for button placement
  const getSelectedElementPosition = (): { top: number; left: number } | null => {
    if (selectedColumnIndex !== null && selectedColumnHeaderRef.current) {
      const rect = selectedColumnHeaderRef.current.getBoundingClientRect()
      return {
        top: rect.bottom + 5,
        left: rect.left + (rect.width / 2)
      }
    } else if (selectedRowIndex !== null && selectedRowIndexRef.current) {
      const rect = selectedRowIndexRef.current.getBoundingClientRect()
      return {
        top: rect.top + (rect.height / 2),
        left: rect.right + 5
      }
    }
    return null
  }

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    insertColumnLeft,
    insertColumnRight,
    insertRowAbove,
    insertRowBelow,
    removeRow: handleRemoveRow,
    getSelectedElementPosition
  }))

  const { columns, rows } = tableData
  const pinnedColumns = tableData.pinnedColumns || []
  const hiddenColumns = tableData.hiddenColumns || []

  // Get visible columns (not hidden) and sort them: pinned first, then others
  const getVisibleColumns = () => {
    const visibleIndices = columns
      .map((_, idx) => idx)
      .filter(idx => !hiddenColumns.includes(idx))
    
    // Sort: pinned columns first (in their original order), then others
    const pinnedVisible = visibleIndices.filter(idx => pinnedColumns.includes(idx))
    const unpinnedVisible = visibleIndices.filter(idx => !pinnedColumns.includes(idx))
    
    return [...pinnedVisible, ...unpinnedVisible]
  }

  const visibleColumnIndices = getVisibleColumns()

  // Handle empty dataset
  if (!columns || columns.length === 0) {
    return (
      <div className="w-full p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
        <p>No columns</p>
      </div>
    )
  }

  return (
    <div className="w-full" style={{ minWidth: 'fit-content' }}>
      <table 
        className="border-collapse border border-gray-300 bg-white" 
        style={{ tableLayout: 'auto' }}
      >
        <thead>
          <tr>
            {/* Row index header cell */}
            <th className="px-2 py-2 text-center font-semibold text-gray-500 border border-gray-300 bg-gray-50 w-12 min-w-[48px]">
              {/* Empty header for row index column */}
            </th>
            {visibleColumnIndices.map((originalIndex) => {
              const column = columns[originalIndex]
              const isPinned = pinnedColumns.includes(originalIndex)
              return (
              <th
                key={originalIndex}
                ref={selectedColumnIndex === originalIndex ? selectedColumnHeaderRef : null}
                onClick={() => {
                  onColumnSelect?.(originalIndex)
                  // Clear row selection when column is selected
                  if (onRowSelect) {
                    onRowSelect(-1)
                  }
                }}
                className={`px-2 py-2 text-left font-semibold text-gray-700 border border-gray-300 relative group cursor-pointer ${
                  selectedColumnIndex === originalIndex 
                    ? (isAIActivated ? 'bg-[#CDEED8]' : 'bg-[#25A26D]/20')
                    : 'bg-gray-100'
                } ${isPinned ? 'bg-blue-50 border-blue-200' : ''}`}
              >
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => handleColumnHeaderBlur(originalIndex, e)}
                  onFocus={() => handleColumnHeaderFocus(originalIndex)}
                  className="w-full bg-transparent border-none outline-none text-sm font-semibold text-gray-700 min-h-[1.5rem] overflow-visible"
                  {...(editingColumnsRef.current.has(originalIndex) ? {} : {
                    dangerouslySetInnerHTML: {
                      __html: `<span class="spreadsheet-table-cell-content" style="direction: ltr !important; unicode-bidi: embed !important; text-align: left; white-space: normal; outline: none; display: inline-block;">${(column || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`
                    }
                  })}
                />
                {columns.length > 1 && (
                  <button
                    onClick={() => handleRemoveColumn(originalIndex)}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                    title="Supprimer la colonne"
                  >
                    <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {isPinned && (
                  <div className="absolute top-1 left-1">
                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  </div>
                )}
              </th>
            )})}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="px-2 py-2 border border-gray-300 bg-gray-50"></td>
              <td
                colSpan={visibleColumnIndices.length}
                className="px-4 py-8 text-center text-gray-500 bg-gray-50"
              >
                No rows
              </td>
            </tr>
          ) : (
            rows.slice(0, visibleRows).map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} group animate-fadeIn ${
                  selectedRowIndex === rowIndex 
                    ? (isAIActivated ? 'bg-[#CDEED8]' : 'bg-[#25A26D]/10')
                    : ''
                }`}
              >
                {/* Row index cell */}
                <td
                  ref={selectedRowIndex === rowIndex ? selectedRowIndexRef : null}
                  onClick={() => {
                    onRowSelect?.(rowIndex)
                    // Clear column selection when row is selected
                    if (onColumnSelect) {
                      onColumnSelect(-1)
                    }
                  }}
                  className={`px-2 py-2 text-center text-gray-500 border border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors w-12 min-w-[48px] ${
                    selectedRowIndex === rowIndex 
                      ? (isAIActivated ? 'bg-[#CDEED8] font-semibold' : 'bg-[#25A26D]/20 font-semibold')
                      : ''
                  }`}
                >
                  {rowIndex + 1}
                </td>
                {visibleColumnIndices.map((originalIndex) => {
                  const isPinned = pinnedColumns.includes(originalIndex)
                  return (
                  <td
                    key={originalIndex}
                    onClick={(e) => {
                      // Select row when clicking on the cell
                      // The contentEditable div will stop propagation when clicked directly
                      onRowSelect?.(rowIndex)
                      // Clear column selection when row is selected
                      if (onColumnSelect) {
                        onColumnSelect(-1)
                      }
                    }}
                    className={`px-2 py-2 text-gray-800 border border-gray-300 align-top cursor-pointer ${
                      isPinned ? 'bg-blue-50/30 border-blue-200' : ''
                    } ${
                      selectedColumnIndex === originalIndex 
                        ? (isAIActivated ? 'bg-[#CDEED8]' : 'bg-[#25A26D]/10')
                        : ''
                    } ${
                      selectedRowIndex === rowIndex 
                        ? (isAIActivated ? 'bg-[#CDEED8]' : 'bg-[#25A26D]/10')
                        : ''
                    }`}
                  >
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => handleCellBlur(rowIndex, originalIndex, e)}
                      onFocus={() => handleCellFocus(rowIndex, originalIndex)}
                      onClick={(e) => {
                        // Stop propagation to prevent row selection when clicking to edit
                        e.stopPropagation()
                      }}
                      className="bg-transparent border-none outline-none text-sm min-h-[1.5rem] overflow-visible focus:bg-white focus:ring-1 focus:ring-[#25A26D] rounded px-1"
                      {...(editingCellsRef.current.has(`${rowIndex}-${originalIndex}`) ? {} : {
                        dangerouslySetInnerHTML: {
                          __html: `<span class="spreadsheet-table-cell-content" style="direction: ltr !important; unicode-bidi: embed !important; text-align: left; white-space: normal; outline: none; display: inline-block;">${(row[originalIndex] || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`
                        }
                      })}
                    />
                  </td>
                )})}
                {rows.length > 1 && (
                  <td className="px-2 py-2 border border-gray-300 bg-gray-50">
                    <button
                      onClick={() => handleRemoveRow(rowIndex)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                      title="Supprimer la ligne"
                    >
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
})

SpreadsheetTable.displayName = 'SpreadsheetTable'

export default SpreadsheetTable
